import { PerformanceMetrics, TestConfig } from './types';
import { resetSocketThrottling } from '../../../services/api/performance';
import { resetButtonCounts } from '../../../services/api';

// Reset metrics for a new test
export const resetMetricsState = (
  clickCountRef: React.MutableRefObject<number>,
  failedClicksRef: React.MutableRefObject<number>,
  responseTimesRef: React.MutableRefObject<number[]>,
  testStartTimeRef: React.MutableRefObject<number>
) => {
  clickCountRef.current = 0;
  failedClicksRef.current = 0;
  responseTimesRef.current = [];
  testStartTimeRef.current = Date.now();
};

// Calculate current metrics based on test progress
export const calculateMetrics = (
  clickCountRef: React.MutableRefObject<number>,
  failedClicksRef: React.MutableRefObject<number>,
  responseTimesRef: React.MutableRefObject<number[]>,
  testStartTimeRef: React.MutableRefObject<number>,
  batchSize?: number
): PerformanceMetrics => {
  const totalClicks = clickCountRef.current;
  const failedClicks = failedClicksRef.current;
  const responseTimesArray = responseTimesRef.current;
  
  const elapsedTime = (Date.now() - testStartTimeRef.current) / 1000; // in seconds
  const clicksPerSecond = elapsedTime > 0 ? totalClicks / elapsedTime : 0;
  
  let averageResponseTime = 0;
  let maxResponseTime = 0;
  
  if (responseTimesArray.length > 0) {
    averageResponseTime = responseTimesArray.reduce((sum, time) => sum + time, 0) / responseTimesArray.length;
    maxResponseTime = Math.max(...responseTimesArray);
  }
  
  const successRate = totalClicks > 0 ? ((totalClicks - failedClicks) / totalClicks) * 100 : 100;
  
  return {
    averageResponseTime,
    maxResponseTime,
    successRate,
    totalClicks,
    clicksPerSecond,
    failedClicks,
    lastBatchSize: clickCountRef.current,
    // Server metrics when available
    serverCpuUsage: 0,
    serverMemoryUsage: 0,
    averageBatchProcessingTime: 0
  };
};

/**
 * Process a batch of button clicks
 */
export const processBatch = (
  buttonIds: string[], 
  counterId: string,
  clickCountRef: React.MutableRefObject<number>,
  failedClicksRef: React.MutableRefObject<number>,
  responseTimesRef: React.MutableRefObject<number[]>,
  testStartTimeRef: React.MutableRefObject<number>,
  setMetrics: (metrics: PerformanceMetrics) => void
) => {
  if (buttonIds.length === 0) return;
  
  // Track request start time
  const startTime = performance.now();
  
  // Create request URL - fix the endpoint
  const url = `${process.env.REACT_APP_API_URL || ''}/api/counters/${counterId}/clicks/batch`;
  
  // Create request body - format for the server's expected structure
  // Group clicks by buttonId as the server expects a clicks object with buttonId -> count mapping
  const groupedClicks: Record<string, number> = {};
  buttonIds.forEach(buttonId => {
    groupedClicks[buttonId] = (groupedClicks[buttonId] || 0) + 1;
  });
  
  // Send POST request to increment button counts in batch
  fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({ clicks: groupedClicks })
  })
    .then(response => {
      if (!response.ok) {
        failedClicksRef.current += buttonIds.length;
        return response.text().then(text => {
          throw new Error(`Error incrementing buttons: ${text}`);
        });
      }
      return response.json();
    })
    .then(data => {
      // Calculate and store response time
      const endTime = performance.now();
      const responseTime = endTime - startTime;
      responseTimesRef.current.push(responseTime);
      
      // Update metrics
      const metrics = calculateMetrics(
        clickCountRef,
        failedClicksRef,
        responseTimesRef,
        testStartTimeRef
      );
      setMetrics(metrics);
    })
    .catch(error => {
      console.error('Batch processing error:', error);
      // Increment failed clicks counter
      failedClicksRef.current += buttonIds.length;
      
      // Update metrics to reflect failures
      const metrics = calculateMetrics(
        clickCountRef,
        failedClicksRef,
        responseTimesRef,
        testStartTimeRef
      );
      setMetrics(metrics);
    });
};

// Reset throttling state before running a test
export const resetThrottlingState = async () => {
  try {
    await resetSocketThrottling();
    console.log('Throttling state reset successfully');
    return true;
  } catch (error) {
    console.error('Error resetting throttling state:', error);
    return false;
  }
};

// Prepare and validate test config
export const validateAndPrepareTestConfig = (testConfig: TestConfig) => {
  // Convert any string values to numbers for the test
  return {
    ...testConfig,
    clicksPerSecond: Number(testConfig.clicksPerSecond),
    testDuration: Number(testConfig.testDuration),
    batchSize: Number(testConfig.batchSize),
    batchInterval: Number(testConfig.batchInterval)
  };
};

// Get test case count for a test suite
export const getTestCaseCount = (
  selectedBatchSizes: number[] | number,
  selectedIntervals: number[] | number
) => {
  // Handle both array and number inputs
  const batchSizesCount = Array.isArray(selectedBatchSizes) 
    ? selectedBatchSizes.length 
    : selectedBatchSizes;
  const intervalsCount = Array.isArray(selectedIntervals) 
    ? selectedIntervals.length 
    : selectedIntervals;
    
  return batchSizesCount * intervalsCount;
};

// Get estimated time for a test suite
export const getEstimatedTime = (
  selectedBatchSizes: number[] | number,
  selectedIntervals: number[] | number,
  testDuration: number | string
) => {
  const testCount = getTestCaseCount(selectedBatchSizes, selectedIntervals);
  if (testCount === 0) return 0;
  
  // Each test takes testDuration seconds plus 1 second cooldown
  const duration = Number(testDuration) || 10;
  return testCount * (duration + 1);
}; 